/**
 * @file inward-grn-list.tsx
 * @module app/modules/inward/components/inward-grn-list
 * @description Inward GRNs with their two parallel approvals (Warehouse photo, Finance
 *   documents). Opens on the queue the user can act on. Tablet-first card layout.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Badge, Button, Card, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import {
  canPerformInwardAction,
  InwardAction,
  isInwardSuperAdmin,
} from "../utils/inward-access";
import {
  approvalLabel,
  GRN_QUEUES,
  GrnQueue,
  InwardGrnRecord,
  isApproved,
  isStockLive,
  totalReceived,
} from "../utils/inward-approval";
import InwardTabs from "./inward-tabs";
import InwardFilterBar, { InwardFilterValue } from "./inward-filter-bar";
import {
  durationBetween,
  inwardStage,
  lineCounts,
  STAGE_LABELS,
} from "../utils/inward-timeline";
import { formatDateTime } from "../utils/inward-timeline";
import "../styles/inward.css";

const PAGE_SIZE = 12;

interface InwardGrnListProps {
  basePath: string;
}

/** First queue the user can act on: Warehouse approvers see theirs, Finance theirs, others all. */
export const defaultQueueFor = (
  user: Parameters<typeof canPerformInwardAction>[0]
): GrnQueue => {
  if (isInwardSuperAdmin(user)) return "";
  if (canPerformInwardAction(user, InwardAction.WarehouseApprove))
    return "warehouse";
  if (canPerformInwardAction(user, InwardAction.FinanceApprove))
    return "finance";
  return "";
};

const ApprovalBadge = ({
  label,
  approved,
}: {
  label: string;
  approved: boolean;
}) => (
  <Badge
    bg=""
    className={`inward-badge fs-13 py-2 px-3 ${
      approved ? "inward-badge--success" : "inward-badge--warning"
    }`}
  >
    {label}: {approved ? "Approved" : "Pending"}
  </Badge>
);

const InwardGrnList = ({ basePath }: InwardGrnListProps) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();

  const [queue, setQueue] = useState<GrnQueue>(() =>
    defaultQueueFor(currentUser)
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [grns, setGrns] = useState<InwardGrnRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<InwardFilterValue>({
    loadingBayManager: "",
    dock: "",
  });

  useEffect(() => {
    const fetchGrns = async () => {
      setLoading(true);
      const response = await new APICallService(
        INWARD.listGrns,
        {
          needCount: true,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          searchTerm: search.trim(),
          queue,
          ...(filters.loadingBayManager
            ? { loadingBayManager: filters.loadingBayManager }
            : {}),
          ...(filters.dock ? { dock: filters.dock } : {}),
        },
        "",
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      setGrns(response?.records ?? []);
      setTotal(response?.total ?? 0);
      setLoading(false);
    };
    const timer = window.setTimeout(fetchGrns, search ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [page, search, queue, filters, selectedWarehouse?._id]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="inward-page" data-testid="inward-grn-list">
      <div className="inward-header">
        <h1 className="fs-22 fw-bolder mb-1">Inward</h1>
        <p className="text-muted fs-14 mb-0">
          GRNs waiting for Warehouse and Finance approval
        </p>
      </div>
      <InwardTabs basePath={basePath} active="grns" />

      <div className="inward-toolbar">
        <Form.Control
          type="search"
          className="inward-search"
          placeholder="Search GRN, PO number or supplier"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          data-testid="grn-search"
        />
        <div
          className="inward-filters"
          role="group"
          aria-label="Approval queue"
        >
          {GRN_QUEUES.map((q) => (
            <Button
              key={q.value || "all"}
              variant={queue === q.value ? "primary" : "outline-primary"}
              className="inward-touch-btn"
              onClick={() => {
                setPage(1);
                setQueue(q.value);
              }}
              data-testid={`grn-queue-${q.value || "all"}`}
            >
              {q.label}
            </Button>
          ))}
        </div>
        <InwardFilterBar
          value={filters}
          onChange={(value) => {
            setPage(1);
            setFilters(value);
          }}
        />
      </div>

      {loading ? (
        <div className="d-flex justify-content-center p-10">
          <Loader loading={loading} />
        </div>
      ) : grns.length === 0 ? (
        <div className="inward-empty" data-testid="grn-empty">
          No GRNs here.
        </div>
      ) : (
        <div className="inward-grid">
          {grns.map((grn) => {
            const open = () =>
              navigate(`${basePath}/grn`, { state: { grnId: grn._id } });
            return (
              <Card
                key={grn._id}
                className="inward-card inward-card-clickable"
                data-testid="grn-card"
                role="link"
                tabIndex={0}
                aria-label={`Open GRN ${grn.refKey}`}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }}
              >
                <Card.Body className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fs-18 fw-bold">GRN-{grn.refKey}</div>
                      <div className="text-muted fs-14">
                        PO {grn.poRefKey || "-"} ·{" "}
                        {grn.date ? formatDateTime(grn.date) : "-"}
                      </div>
                    </div>
                    <Badge
                      bg=""
                      className={`inward-badge inward-badge--${
                        STAGE_LABELS[inwardStage(grn)].variant
                      } fs-13 py-2 px-3 text-wrap text-end`}
                      data-testid="grn-stage"
                    >
                      {STAGE_LABELS[inwardStage(grn)].label}
                    </Badge>
                  </div>
                  <dl className="inward-facts mb-0">
                    <dt>Supplier</dt>
                    <dd>
                      {grn.businessDetails?.ownerName ||
                        grn.businessDetails?.name ||
                        "-"}
                    </dd>
                    <dt>Received</dt>
                    <dd>{totalReceived(grn)}</dd>
                    <dt>Loading bay</dt>
                    <dd data-testid="grn-manager">
                      {grn.inward?.loadingBayManager?.name || "-"}
                    </dd>
                    <dt>Dock</dt>
                    <dd data-testid="grn-dock">
                      {grn.inward?.dock?.name || "-"}
                    </dd>
                    <dt>Waiting</dt>
                    <dd data-testid="grn-waiting">
                      {isStockLive(grn)
                        ? `Done in ${durationBetween(
                            grn.inward?.doneAt ?? grn._createdAt ?? grn.date,
                            grn.inward?.stockLiveAt
                          )}`
                        : durationBetween(
                            grn.inward?.doneAt ?? grn._createdAt ?? grn.date
                          )}
                    </dd>
                  </dl>
                  <div
                    className="d-flex flex-wrap gap-2 mt-auto"
                    data-testid="grn-approvals"
                  >
                    <ApprovalBadge
                      label="Warehouse"
                      approved={isApproved(grn.inward?.warehouseApproval)}
                    />
                    <ApprovalBadge
                      label={
                        isApproved(grn.inward?.financeApproval)
                          ? "Finance"
                          : `Finance ${lineCounts(grn).approved}/${
                              lineCounts(grn).total
                            }`
                      }
                      approved={isApproved(grn.inward?.financeApproval)}
                    />
                  </div>
                  <span className="visually-hidden">
                    Warehouse {approvalLabel(grn.inward?.warehouseApproval)},
                    Finance {approvalLabel(grn.inward?.financeApproval)}
                  </span>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="inward-pager">
          <Button
            variant="outline-primary"
            className="inward-touch-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="fs-15 fw-semibold">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            className="inward-touch-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default InwardGrnList;
